import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, Trash2, X, Edit3, Search, Loader2, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * AdminMasterOccasionsPage - Slice D (Iter 9.40)
 * Full CRUD over db.master_occasions with search-pickers (no free text).
 */
export default function AdminMasterOccasionsPage() {
  const [occasions, setOccasions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [occ, cats, exps] = await Promise.all([
        fetch(`${API}/admin/master-occasions`, { headers: auth() }).then((r) => r.json()).catch(() => ({})),
        fetch(`${API}/admin/categories`, { headers: auth() }).then((r) => r.json()).catch(() => ({})),
        fetch(`${API}/admin/master-experiences`, { headers: auth() }).then((r) => r.json()).catch(() => ({})),
      ]);
      setOccasions(occ.occasions || []);
      setCategories((Array.isArray(cats) ? cats : cats.categories) || []);
      setExperiences(exps.experiences || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (o) => { setEditingId(o.occasion_id || o.slug); setDraft({ ...o }); };
  const cancel = () => { setEditingId(null); setDraft(null); };
  const startNew = () => {
    setEditingId("__new__");
    setDraft({ name: "", slug: "", description: "", image_url: "", linked_categories: [], linked_experiences: [], default_experiences: [], sort_order: occasions.length + 1, active: true });
  };

  const save = async () => {
    if (!draft?.name?.trim()) { toast.error("Name required"); return; }
    const isNew = editingId === "__new__";
    const url = isNew ? `${API}/admin/master-occasions` : `${API}/admin/master-occasions/${editingId}`;
    const method = isNew ? "POST" : "PUT";
    const r = await fetch(url, { method, headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify(draft) });
    if (r.ok) { toast.success(`Occasion ${isNew ? "created" : "updated"}`); cancel(); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Save failed"); }
  };

  const remove = async (oid) => {
    if (!window.confirm("Delete this occasion?")) return;
    const r = await fetch(`${API}/admin/master-occasions/${oid}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success("Deleted"); load(); } else { toast.error("Delete failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "occasions");
    const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
    if (r.ok) { const d = await r.json(); setDraft((p) => ({ ...p, image_url: d.secure_url || d.url })); }
    else { toast.error("Upload failed"); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-4" data-testid="admin-master-occasions">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Master Occasions</h2>
          <p className="text-xs text-stone-500">Life-moment bundles that link preference cards to multiple categories + experiences. Occasions are NOT a host-dashboard category - they're the per-card link concept.</p>
        </div>
        <Button onClick={startNew} className="bg-purple-600 text-white" data-testid="mo-new"><Plus className="w-4 h-4 mr-1" /> New occasion</Button>
      </header>

      {editingId === "__new__" && (
        <OccasionEditor draft={draft} setDraft={setDraft} categories={categories} experiences={experiences} onSave={save} onCancel={cancel} onUpload={uploadImage} testidPrefix="mo-new" />
      )}
      <div className="space-y-2">
        {occasions.map((o) => {
          const oid = o.occasion_id || o.slug;
          if (editingId === oid) return <OccasionEditor key={oid} draft={draft} setDraft={setDraft} categories={categories} experiences={experiences} onSave={save} onCancel={cancel} onUpload={uploadImage} testidPrefix={`mo-edit-${oid}`} />;
          return (
            <Card key={oid} data-testid={`mo-row-${oid}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-md bg-stone-100 overflow-hidden flex items-center justify-center shrink-0">
                  {o.image_url ? <img src={o.image_url} alt={o.name} className="w-full h-full object-cover" /> : <ImgIcon className="w-5 h-5 text-stone-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{o.name} {o.active === false && <span className="text-[10px] text-stone-400 font-normal ml-1">(inactive)</span>}</p>
                  <p className="text-[11px] text-stone-500 truncate">{o.description || "-"}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] text-stone-400">{(o.linked_categories || []).length} cats · {(o.linked_experiences || []).length} exps</span>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => startEdit(o)} data-testid={`mo-edit-${oid}-btn`}><Edit3 className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(oid)} data-testid={`mo-del-${oid}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function OccasionEditor({ draft, setDraft, categories, experiences, onSave, onCancel, onUpload, testidPrefix }) {
  const update = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  return (
    <Card data-testid={testidPrefix}>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Name</Label>
            <Input value={draft.name || ""} onChange={(e) => update("name", e.target.value)} data-testid={`${testidPrefix}-name`} />
          </div>
          <div>
            <Label className="text-[11px]">Slug</Label>
            <Input value={draft.slug || ""} onChange={(e) => update("slug", e.target.value)} placeholder="auto from name" data-testid={`${testidPrefix}-slug`} />
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Description</Label>
          <Textarea rows={2} value={draft.description || ""} onChange={(e) => update("description", e.target.value)} data-testid={`${testidPrefix}-desc`} />
        </div>
        <div>
          <Label className="text-[11px]">Hero image</Label>
          <div className="flex items-center gap-2">
            <div className="w-16 h-16 rounded-md bg-stone-100 overflow-hidden flex items-center justify-center">
              {draft.image_url ? <img src={draft.image_url} alt="" className="w-full h-full object-cover" /> : <ImgIcon className="w-5 h-5 text-stone-300" />}
            </div>
            <input type="file" accept="image/*" onChange={(e) => onUpload(e.target.files?.[0])} data-testid={`${testidPrefix}-image`} />
          </div>
        </div>
        <SearchPicker label="Linked categories" items={categories.map((c) => ({ id: c.category_id, label: c.label || c.name || c.category_id, image: c.icon_url }))} value={draft.linked_categories || []} onChange={(v) => update("linked_categories", v)} testidPrefix={`${testidPrefix}-cats`} />
        <SearchPicker label="Linked experiences" items={experiences.map((e) => ({ id: e.experience_id || e.slug, label: e.name }))} value={draft.linked_experiences || []} onChange={(v) => update("linked_experiences", v)} testidPrefix={`${testidPrefix}-exps`} />
        <SearchPicker label="Default experiences" items={experiences.map((e) => ({ id: e.experience_id || e.slug, label: e.name }))} value={draft.default_experiences || []} onChange={(v) => update("default_experiences", v)} testidPrefix={`${testidPrefix}-default-exps`} />
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-[11px]">Sort</Label>
            <Input type="number" className="w-20" value={draft.sort_order ?? 0} onChange={(e) => update("sort_order", parseInt(e.target.value || "0", 10))} data-testid={`${testidPrefix}-sort`} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={draft.active !== false} onCheckedChange={(v) => update("active", v)} data-testid={`${testidPrefix}-active`} />
            <Label className="text-[11px]">Active</Label>
          </div>
        </div>
        <div className="flex gap-2 pt-2 border-t border-stone-100">
          <Button onClick={onSave} className="bg-purple-600 text-white" data-testid={`${testidPrefix}-save`}><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
          <Button variant="outline" onClick={onCancel} data-testid={`${testidPrefix}-cancel`}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SearchPicker({ label, items, value, onChange, testidPrefix }) {
  const [q, setQ] = useState("");
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return items.filter((it) => (it.label || "").toLowerCase().includes(s) && !value.includes(it.id)).slice(0, 8);
  }, [q, items, value]);
  const labelFor = (id) => items.find((it) => it.id === id)?.label || id;
  return (
    <div data-testid={testidPrefix}>
      <Label className="text-[11px]">{label} ({value.length})</Label>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-stone-400" />
        <Input className="pl-7" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} data-testid={`${testidPrefix}-search`} />
      </div>
      {matches.length > 0 && (
        <div className="border border-stone-200 rounded mt-1 bg-white divide-y divide-stone-100">
          {matches.map((m) => (
            <button key={m.id} type="button" onClick={() => { onChange([...value, m.id]); setQ(""); }} className="w-full text-left px-2 py-1.5 hover:bg-stone-50 flex items-center gap-2" data-testid={`${testidPrefix}-add-${m.id}`}>
              {m.image && <img src={m.image} alt="" className="w-4 h-4 rounded object-contain" />}
              <span className="text-xs">{m.label}</span>
              <Plus className="w-3 h-3 ml-auto text-stone-400" />
            </button>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((id) => (
            <span key={id} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-100 flex items-center gap-1" data-testid={`${testidPrefix}-tag-${id}`}>
              {labelFor(id)}
              <button type="button" onClick={() => onChange(value.filter((v) => v !== id))} className="text-purple-400 hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
